import { Injectable } from "@angular/core";
import { SupabaseClient, createClient } from "@supabase/supabase-js";
import { environment } from "src/environments/environment";
import { SendRequest } from "src/app/office/interfaces/send_request";
import { CreatePaymentRequest } from "src/app/office/interfaces/create_payment_request";
import { LoadingController, ToastController } from "@ionic/angular";
import { updateCustomer } from "src/app/auth/ngrx/customer.actions";
import { CustomerDto } from "src/app/auth/interfaces/customer_dto";
import { Store } from "@ngrx/store";
import { AuthStorage } from "src/app/core/storage/auth-storage.class";
import { Observable } from "rxjs/internal/Observable";

@Injectable({
  providedIn: "root",
})
export class FunctionService {
  private supabase: SupabaseClient;

  constructor(private loadingCtrl: LoadingController, private toastController: ToastController, private store: Store<any>) {
    this.supabase = createClient(environment.supabaseClientConfig.supabaseUrl, environment.supabaseClientConfig.supabaseKey, {
      auth: {
        storage: new AuthStorage(),
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false
      }
    })
  }

  sendMail(
    sendRequest: SendRequest,
    payOnline: boolean
  ) {
    return new Observable((observer) => {
      let requestToSend = { ...sendRequest };

      if (payOnline) {
        let createPaymentRequest: CreatePaymentRequest = {
          amount: sendRequest.total,
          customer: sendRequest.customer,
          invoice_id: sendRequest.invoiceId,
          subject: sendRequest.subject,
          mail: sendRequest.recipient
        };

        this.createPaymentLink(createPaymentRequest).then((paymentLink) => {
          const url = paymentLink.sessionUrl.split("?")[0];
          const stripePaymentId = url.split("/").pop();
          const newUrl = `https://app.quickdrive.ch/stripe_payment_id=${stripePaymentId}`;
          requestToSend.payment_link = newUrl;
          this.supabase.functions.invoke(
            "smtp2sendmail",
            { body: requestToSend }
          ).then((response) => {
            observer.next(response.data);
          }, (error) => {
            observer.error(error);
          });
        }, (error) => {
          observer.error(error);
        });
      } else {
        this.supabase.functions.invoke(
          "smtp2sendmail",
          { body: requestToSend }
        ).then((response) => {
          observer.next(response.data);
        }, (error) => {
          observer.error(error);
        });
      }
    });
  }

  async createPaymentLink(createPaymentRequest: CreatePaymentRequest) {
    const { data, error } = await this.supabase.functions.invoke(
      "stripepayments",
      { body: createPaymentRequest }
    );

    if (error) {
      throw error;
    }

    return data;
  }

  async connectStripeAccount(customer: CustomerDto) {
    const loading = await this.loadingCtrl.create({
      message: "Mit Stripe verbinden...",
    });
    
    loading.present();
    const { data, error } = await this.supabase.functions.invoke(
      "stripe",
      { body: {} }
    );
    
    if (error) {
      let updatedCustomer = {
        ...customer,
        customer_id: customer.customer_id,
        user_id: customer.user_id,
        stripe_onboarding_completed: false,
        stripe_connect_id: null
      }
      this.store.dispatch(updateCustomer({ customer: updatedCustomer }));
      loading.dismiss();
      this.presentError();
      throw error;
    }
    loading.dismiss();
    if (data.account_link && data.account_id) {
      let updatedCustomer = {
        ...customer,
        stripe_connect_id: data.account_id
      }
      this.store.dispatch(updateCustomer({ customer: updatedCustomer }));
    } else {
      let updatedCustomer = {
        ...customer,
        stripe_onboarding_completed: true
      };
      this.store.dispatch(updateCustomer({ customer: updatedCustomer }));
      await this.presentSuccessAuth();
    }

    return data;
  }

  async presentSuccessAuth() {
    const toast = await this.toastController.create({
      message: "Stripe Verbindung erfolgreich",
      duration: 2000,
      color: "success",
    });
    toast.present();
  }

  async presentError() {
    const toast = await this.toastController.create({
      message: "Stripe Verbindung fehlgeschlagen",
      duration: 2000,
      color: "danger",
    });
    toast.present();
  }

  async refreshGoogleToken(providerRefreshToken: string) {
    const { data, error } = await this.supabase.functions.invoke(
      'refresh-google-token',
      {
        body: {
          providerRefreshToken,
        },
      }
    );

    if (error) {
      throw error;
    }

    return data;
  }

  async getLfaData(files: File[], data: any): Promise<any> {
    if (!files || files.length === 0) {
      throw new Error("No files provided");
    }

    const loading = await this.loadingCtrl.create({
      message: "Analysiere Lernfahrausweis...",
    });
    
    await loading.present();
    
    try {
      const formData = new FormData();
      formData.append("image", files[0]);
      formData.append("prompt", "Extract the following information from this Swiss learner's driving license (Lernfahrausweis): firstname, lastname, address, birthdate, category, license number, PIN, PID, start date, end date. Format the response as JSON with the fields: forename, surname, street, postal_code, city, birthday, category, lfa_number, lfa_pin, lfa_pid, lfa_start, lfa_end. Dates should be in YYYY-MM-DD format.");
      
      const { data: responseData, error } = await this.supabase.functions.invoke(
        "gemini-vision",
        {
          body: formData,
        }
      );
      
      loading.dismiss();
      
      if (error) {
        await this.presentLfaError();
        throw error;
      }
      
      // Extract the text content from Gemini's response
      let extractedData = {};
      if (responseData.candidates && responseData.candidates[0]?.content?.parts) {
        const textContent = responseData.candidates[0].content.parts[0].text;
        
        try {
          // Try to parse the JSON response
          const cleanContent = textContent.replace(/```json\n/, '').replace(/\n```/, '');
          extractedData = JSON.parse(cleanContent);
        } catch (e) {
          // If JSON parsing fails, try to extract data using regex
          extractedData = this.extractDataFromText(textContent);
        }
      }
      
      await this.presentLfaSuccess();
      return extractedData;
    } catch (error) {
      loading.dismiss();
      await this.presentLfaError();
      throw error;
    }
  }

  private extractDataFromText(text: string): any {
    const result: any = {};
    
    // Extract license number (typically numeric)
    const lfaNumberMatch = text.match(/lfa_number["\s:]+([0-9]+)/i) || 
                           text.match(/license number["\s:]+([0-9]+)/i) ||
                           text.match(/number["\s:]+([0-9]+)/i);
    if (lfaNumberMatch) result.lfa_number = lfaNumberMatch[1];
    
    // Extract PIN (typically numeric)
    const lfaPinMatch = text.match(/lfa_pin["\s:]+([0-9]+)/i) || 
                        text.match(/pin["\s:]+([0-9]+)/i);
    if (lfaPinMatch) result.lfa_pin = lfaPinMatch[1];
    
    // Extract PID (typically numeric)
    const lfaPidMatch = text.match(/lfa_pid["\s:]+([0-9]+)/i) || 
                        text.match(/pid["\s:]+([0-9]+)/i);
    if (lfaPidMatch) result.lfa_pid = lfaPidMatch[1];
    
    // Extract start date (in YYYY-MM-DD format)
    const lfaStartMatch = text.match(/lfa_start["\s:]+([0-9]{4}-[0-9]{2}-[0-9]{2})/i) || 
                          text.match(/start date["\s:]+([0-9]{4}-[0-9]{2}-[0-9]{2})/i) ||
                          text.match(/start["\s:]+([0-9]{4}-[0-9]{2}-[0-9]{2})/i);
    if (lfaStartMatch) result.lfa_start = lfaStartMatch[1];
    
    // Extract end date (in YYYY-MM-DD format)
    const lfaEndMatch = text.match(/lfa_end["\s:]+([0-9]{4}-[0-9]{2}-[0-9]{2})/i) || 
                        text.match(/end date["\s:]+([0-9]{4}-[0-9]{2}-[0-9]{2})/i) ||
                        text.match(/end["\s:]+([0-9]{4}-[0-9]{2}-[0-9]{2})/i);
    if (lfaEndMatch) result.lfa_end = lfaEndMatch[1];
    
    return result;
  }

  async presentLfaSuccess() {
    const toast = await this.toastController.create({
      message: "Lernfahrausweis erfolgreich analysiert",
      duration: 2000,
      color: "success",
    });
    toast.present();
  }

  async presentLfaError() {
    const toast = await this.toastController.create({
      message: "Fehler bei der Analyse des Lernfahrausweises",
      duration: 2000,
      color: "danger",
    });
    toast.present();
  }
}
